import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/hero-storage-aerial.webp";

const ApexHero = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const navigate = useNavigate();

  // Preload the image
  useEffect(() => {
    const img = new Image();
    img.src = heroImage;
    img.onload = () => setImageLoaded(true);
  }, []);

  const navigateToBunkerWalls = () => {
    navigate("/collections/bunker-walls");
  };

  const navigateToParts = () => {
    navigate("/collections/parts");
  };

  return (
    <section className="relative h-screen flex items-end justify-center overflow-hidden">
      {/* Background Image with blur-up loading */}
      <div className="absolute inset-0">
        {/* Low-quality placeholder background */}
        <div 
          className={`absolute inset-0 bg-gradient-to-b from-zinc-800 to-background transition-opacity duration-700 ${imageLoaded ? 'opacity-0' : 'opacity-100'}`}
        />
        
        <img
          src={heroImage}
          alt="TSX Temporary Storage System"
          width={1600}
          height={900}
          className={`w-full h-full object-cover transition-opacity duration-700 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        {/* Darker Modern Gradient Overlay - blends seamlessly into background */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(to bottom, 
                rgba(0, 0, 0, 0.6) 0%,
                rgba(0, 0, 0, 0.4) 30%,
                rgba(0, 0, 0, 0.5) 60%,
                hsl(0 0% 4%) 100%
              ),
              linear-gradient(to right,
                rgba(0, 0, 0, 0.6) 0%,
                rgba(0, 0, 0, 0.2) 50%,
                rgba(0, 0, 0, 0.5) 100%
              )
            `
          }}
        />
        {/* Stronger vignette effect */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at center, transparent 0%, rgba(0, 0, 0, 0.6) 100%)"
          }}
        />
      </div>

      {/* Content - Centered at bottom */}
      <div className="relative z-10 text-center pb-24 md:pb-32 px-6">
        {/* Headline */}
        <h1 className="font-display uppercase tracking-wide mb-12 md:mb-16 animate-fade-up">
          <span className="block text-4xl md:text-5xl lg:text-7xl font-black text-white mb-4 md:mb-6">
            Industrial Grade
          </span>
          <span className="block text-4xl md:text-5xl lg:text-7xl font-black text-primary mb-6 md:mb-8">
            Temporary Storage.
          </span>
          <span className="block text-xl md:text-2xl lg:text-3xl font-semibold text-white/80 tracking-widest">
            Deploy in hours. Engineered for harvest.
          </span>
        </h1>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <Button 
            onClick={navigateToBunkerWalls}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-display font-semibold uppercase tracking-widest px-8 py-6 text-base rounded-md group"
          >
            Shop Bunker Walls
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button 
            onClick={navigateToParts}
            variant="outline"
            className="border-white/30 text-white hover:bg-white/10 font-display font-semibold uppercase tracking-widest px-8 py-6 text-base rounded-md"
          >
            View Parts
          </Button>
        </div>
      </div>

      {/* Scroll Indicator - Chevron */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-8 h-8 text-primary" />
      </div>
    </section>
  );
};

export default ApexHero;